import { useNavigate } from "react-router-dom";
import { EMPLOYEE_ROLE_CONFIG } from "@/constants/employees";
import type { Employee } from "@/types";
import EmployeeStatusBadge from "@/pages/employees/components/EmployeeStatusBadge";

interface BranchEmployeesTabProps {
  branchId: string;
  employees: Employee[];
}

export default function BranchEmployeesTab({
  branchId,
  employees,
}: BranchEmployeesTabProps) {
  const navigate = useNavigate();
  const listPath = `/employees?branch=${branchId}`;

  const addButton = (
    <button
      type="button"
      onClick={() => navigate(`/employees/new?branch=${branchId}`)}
      className="inline-flex items-center justify-center gap-2 h-10 rounded-btn bg-primary-500 px-4 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
    >
      <i className="ri-user-add-line"></i>
      إضافة موظف للفرع
    </button>
  );

  if (employees.length === 0) {
    return (
      <div className="rounded-box border border-dashed border-background-300 px-6 py-12 text-center">
        <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
          <i className="ri-team-line text-2xl"></i>
        </div>
        <p className="text-sm font-semibold text-foreground-900">
          لا يوجد موظفون في هذا الفرع
        </p>
        <p className="mt-1 text-xs text-foreground-500">
          أضف موظفي الفرع وحدد أدوارهم وصلاحياتهم.
        </p>
        <div className="mt-4 flex justify-center">{addButton}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
          {employees.length} موظف في الفرع
        </span>
        {addButton}
      </div>

      <ul className="space-y-2.5">
        {employees.map((employee) => {
          const role = EMPLOYEE_ROLE_CONFIG[employee.role];
          return (
            <li
              key={employee.id}
              className="flex flex-wrap items-center gap-3 rounded-btn border border-background-200 px-4 py-3"
            >
              <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold">
                {employee.name.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-950">
                  {employee.name}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${role.className}`}
                  >
                    <i className={role.icon}></i>
                    {role.label}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-0.5 text-xs font-medium text-foreground-600 whitespace-nowrap">
                    <i className="ri-shield-check-line"></i>
                    {employee.permissions.length} صلاحية
                  </span>
                  <EmployeeStatusBadge status={employee.status} />
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/employees/${employee.id}/edit`)}
                className="inline-flex items-center gap-1.5 h-9 rounded-btn bg-background-100 px-3 text-xs font-semibold text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line"></i>
                تعديل
              </button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={() => navigate(listPath)}
        className="text-xs font-semibold text-primary-600 hover:underline cursor-pointer"
      >
        إدارة كل الموظفين والصلاحيات
      </button>
    </div>
  );
}